import React from "react";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import CelebrationIcon from "@mui/icons-material/Celebration";
import EquipmentChip from "./EquipmentChip";
import type { Equipment } from "../../../../EquipmentsTab/types/types";
import { useTranslation } from "react-i18next";

interface EquipmentDisplayProps {
	selectedEquipments: Array<{
		equipment: Equipment;
		count: number;
	}>;
	editingEquipmentIndex: number | null;
	hoveredChipIndex: number | null;
	isSubmitting: boolean;
	totalEquipmentCount: number;
	canAddEquipment: boolean;
	onMouseEnter: (index: number) => void;
	onMouseLeave: () => void;
	onStartEdit: (index: number) => void;
	onRemoveEquipment: (index: number) => void;
}

const EquipmentDisplay: React.FC<EquipmentDisplayProps> = ({
	selectedEquipments,
	editingEquipmentIndex,
	hoveredChipIndex,
	isSubmitting,
	totalEquipmentCount,
	canAddEquipment,
	onMouseEnter,
	onMouseLeave,
	onStartEdit,
	onRemoveEquipment,
}) => {
	const { t } = useTranslation();
	// Render empty state when no equipment is selected
	const renderEmptyState = () => (
		<div className="text-center ">
			<div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
				<ConstructionOutlinedIcon className="text-green-400 mb-4" fontSize="large" />
				<h3 className="text-lg font-semibold text-gray-800 mb-2">
					{t("feature.courseBuilder.labs.createLab.noEquipmentSelected")}
				</h3>
				<p className="text-gray-600 mb-4">{t("feature.courseBuilder.labs.createLab.pleaseSelectEquipment")}</p>
			</div>
		</div>
	);

	// Render success state when all equipment is added
	const renderSuccessState = () => (
		<div className="text-center ">
			<div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-2 border border-green-200">
				<CelebrationIcon className="text-green-500 mb-4" fontSize="large" />
				<h3 className="text-lg font-semibold text-gray-800 mb-2">
					{t("feature.courseBuilder.labs.createLab.allEquipmentAdded")}
				</h3>
				<p className="text-gray-600 mb-4">
					{t("feature.courseBuilder.labs.createLab.totalEquipmentCount", { totalEquipmentCount })}
				</p>
			</div>
		</div>
	);

	return (
		<div className="space-y-3">
			{selectedEquipments.length > 0 ? (
				<>
					<div className="flex items-center gap-2 p-2">
						<ConstructionOutlinedIcon className="text-gray-500 text-sm" />
						<span className="text-sm font-medium text-gray-700">
							{t("feature.courseBuilder.labs.createLab.selectedEquipment", {
								totalEquipmentCount: selectedEquipments.length,
								totalEquipmentUnits: totalEquipmentCount,
							})}
						</span>
					</div>

					<div className="flex flex-wrap gap-3 p-2 overflow-y-auto max-h-[100px]">
						{selectedEquipments.map((item, index) => (
							<EquipmentChip
								key={index}
								item={item}
								index={index}
								isEditing={editingEquipmentIndex === index}
								isHovered={hoveredChipIndex === index}
								isSubmitting={isSubmitting}
								onMouseEnter={() => onMouseEnter(index)}
								onMouseLeave={onMouseLeave}
								onStartEdit={onStartEdit}
								onRemoveEquipment={onRemoveEquipment}
							/>
						))}
					</div>
				</>
			) : (
				renderEmptyState()
			)}

			{/* Success state when all equipment is added */}
			{selectedEquipments.length > 0 && !canAddEquipment && renderSuccessState()}
		</div>
	);
};

export default EquipmentDisplay;
